import { Box, Skeleton } from "@mui/material";
import { type FC, useState } from "react";
import { CONTROL_HEIGHT } from "@/theme/tokens";
import { type RandomRange, randomBetween, randomCount } from "./skeletonVariants";

/** How the form varies per mount: its section and field counts, each section title's width and each field's width (% of the form). */
const FORM_RANGES = {
	sections: [2, 3],
	titleWidth: [20, 35],
	fields: [3, 5],
	fieldWidth: [60, 100],
} as const satisfies Record<string, RandomRange>;

/** A settings page: sections, each a title over labelled fields, and an action row at the foot. */
export const FormSkeletonVariant: FC = () => {
	const [sections] = useState(() =>
		Array.from({ length: randomCount(FORM_RANGES.sections) }, () => ({
			titleWidth: randomBetween(FORM_RANGES.titleWidth),
			fieldWidths: Array.from({ length: randomCount(FORM_RANGES.fields) }, () => randomBetween(FORM_RANGES.fieldWidth)),
		})),
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
			{sections.map((section, sectionIndex) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: placeholder sections are a fixed form; nothing reorders them.
				<Box key={sectionIndex} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Skeleton variant="text" width={`${section.titleWidth}%`} sx={{ typography: "h6" }} />
					{section.fieldWidths.map((fieldWidth, fieldIndex) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: placeholder fields are a fixed form; nothing reorders them.
						<Box key={fieldIndex} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
							<Skeleton variant="text" width="25%" />
							<Skeleton variant="rounded" sx={{ width: `${fieldWidth}%`, height: CONTROL_HEIGHT.STANDARD, borderRadius: 1 }} />
						</Box>
					))}
				</Box>
			))}
			<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
				<Skeleton variant="rounded" sx={{ width: "12%", height: CONTROL_HEIGHT.STANDARD, borderRadius: 1 }} />
				<Skeleton variant="rounded" sx={{ width: "12%", height: CONTROL_HEIGHT.STANDARD, borderRadius: 1 }} />
			</Box>
		</Box>
	);
};
